import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AutoTextarea, Field, ListEditor } from "./shared";
import { Button } from "../../ui/button";
import { API } from "../../../App";
import { ShieldAlert, Dumbbell, Plus, Check, Loader2 } from "lucide-react";

const SuggestedExercises = ({ items, catalog, patientId }) => {
  const [done, setDone] = useState({});
  const [busy, setBusy] = useState("");
  const labelOf = (id) => catalog.find((c) => c.type_id === id)?.label || id;
  const assign = async (ex) => {
    setBusy(ex.type_id);
    try {
      await axios.post(`${API}/exercises/assign`, { exercise_type: ex.type_id, patient_id: patientId, title: labelOf(ex.type_id), instructions: ex.reason || "" }, { withCredentials: true });
      setDone((d) => ({ ...d, [ex.type_id]: true })); toast.success(`Exercice « ${labelOf(ex.type_id)} » assigné`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur d'assignation"); }
    finally { setBusy(""); }
  };
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border border-sky-200 dark:border-sky-900/50 bg-sky-50/40 dark:bg-sky-950/10 p-3 space-y-2" data-testid="suggested-exercises">
      <h4 className="text-sm font-semibold flex items-center gap-1.5 text-sky-900 dark:text-sky-100"><Dumbbell className="w-4 h-4" /> Exercices proposés à l'adolescent(e)</h4>
      <ul className="space-y-1.5">
        {items.filter((ex) => ex?.type_id).map((ex) => (
          <li key={ex.type_id} className="flex items-start justify-between gap-3 text-sm" data-testid={`suggested-exercise-${ex.type_id}`}>
            <div><span className="font-medium">{labelOf(ex.type_id)}</span>{ex.reason && <span className="text-muted-foreground"> — {ex.reason}</span>}</div>
            <Button size="sm" variant={done[ex.type_id] ? "secondary" : "outline"} disabled={busy !== "" || done[ex.type_id]} onClick={() => assign(ex)} className="gap-1 shrink-0 h-7" data-testid={`assign-exercise-${ex.type_id}`}>
              {busy === ex.type_id ? <Loader2 className="w-3 h-3 animate-spin" /> : done[ex.type_id] ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {done[ex.type_id] ? "Assigné" : "Assigner"}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Formulaire générique par sections (guidance parentale) : textes par section + messages clés + exercices suggérés + notes confidentielles. */
export const SectionsForm = ({ data, onChange, specs, config, report }) => {
  const sections = specs[config.sections] || [];
  const setSection = (k, v) => onChange({ ...data, sections: { ...(data.sections || {}), [k]: v } });
  return (
    <div className="space-y-4" data-testid="sections-form">
      <SuggestedExercises items={data.suggested_exercises} catalog={specs.ado_exercises || []} patientId={report?.patient_id} />
      <Field label="Messages clés pour les parents" hint="Un message par ligne — repris en tête du PDF">
        <ListEditor value={data.key_points} onChange={(v) => onChange({ ...data, key_points: v })} testId="sections-key-points" />
      </Field>
      <div className="grid md:grid-cols-2 gap-3">
        {sections.map((sec) => (
          <div key={sec.key} className="rounded-xl border p-3 space-y-1.5 bg-card" data-testid={`section-${sec.key}`}>
            <h4 className="text-sm font-semibold">{sec.label}</h4>
            <p className="text-[11px] text-muted-foreground">{sec.hint}</p>
            <AutoTextarea value={data.sections?.[sec.key] || ""} onChange={(v) => setSection(sec.key, v)} rows={4} testId={`section-${sec.key}-text`} />
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-amber-300 bg-amber-50/50 dark:bg-amber-950/20 p-3 space-y-1.5" data-testid="sections-confidential">
        <h4 className="text-sm font-semibold flex items-center gap-1.5 text-amber-800 dark:text-amber-200"><ShieldAlert className="w-4 h-4" /> Notes réservées au thérapeute</h4>
        <p className="text-[11px] text-muted-foreground">Ce qu'il ne faut pas transmettre aux parents et pourquoi, vigilance sur la dynamique familiale. Jamais exporté dans le PDF.</p>
        <AutoTextarea value={data.confidential_notes || ""} onChange={(v) => onChange({ ...data, confidential_notes: v })} rows={3} testId="sections-confidential-text" />
      </div>
    </div>
  );
};
